/**
 * Helper functions for hiding/unhiding questions logic
 */

/**
 * Collects grid hide questions from a grid answer
 * @param {Object} gridAnswer - The grid answer object (rowId -> optionId mapping)
 * @param {Object} question - The question object with options
 * @returns {Array} Array of question indices to hide
 */
export const collectGridHideQuestions = (gridAnswer, question) => {
  const hideQues = [];

  if (
    gridAnswer &&
    typeof gridAnswer === "object" &&
    Object.keys(gridAnswer)?.length > 0
  ) {
    Object.keys(gridAnswer)?.forEach((rowId) => {
      const selectedOptionId = gridAnswer[rowId];

      // Handle both single selection and multi-selection
      const selectedOptionIds = Array.isArray(selectedOptionId)
        ? selectedOptionId
        : [selectedOptionId];

      selectedOptionIds?.forEach((optionId) => {
        // Find the option that matches the selected optionId
        const selectedOption = question?.options?.find(
          (option) => option._id === optionId
        );

        // Check if this option has grid_hide_questions array
        if (
          selectedOption &&
          selectedOption?.grid_hide_questions &&
          Array.isArray(selectedOption?.grid_hide_questions)
        ) {
          // Find the specific hide question configuration for this rowId
          const hideQuestionConfig = selectedOption.grid_hide_questions.find(
            (config) => config.rowId === rowId
          );

          // If found, add the hide_questions for this rowId
          if (
            hideQuestionConfig &&
            hideQuestionConfig.hide_questions &&
            Array.isArray(hideQuestionConfig.hide_questions)
          ) {
            hideQues.push(...hideQuestionConfig.hide_questions);
          }
        }
      });
    });
  }

  return hideQues;
};

/**
 * Updates question history by removing hidden questions and tracking their positions
 * @param {Array} currentHistory - Current question history array
 * @param {Array} hiddenQuestions - Array of questions to hide
 * @param {Object} hiddenQuestionsMap - Map of already hidden questions
 * @returns {Object} { updatedHistory, updatedMap, questionsToUnhide }
 */
export const processHiddenQuestionsInHistory = (
  currentHistory,
  hiddenQuestions,
  hiddenQuestionsMap
) => {
  const newHiddenQuestionsMap = { ...hiddenQuestionsMap };
  const updatedQuestionHistory = [];

  // Remove hidden questions from history and track their positions
  currentHistory.forEach((historyItem, index) => {
    const isHiddenQuestion = hiddenQuestions.some(
      (hiddenQue) =>
        hiddenQue._id === historyItem._id ||
        hiddenQue._id === historyItem.question?._id
    );

    if (!isHiddenQuestion) {
      updatedQuestionHistory.push(historyItem);
    } else {
      // Track this hidden question with its original position
      const questionId = historyItem._id || historyItem.question?._id;
      if (questionId && !newHiddenQuestionsMap[questionId]) {
        newHiddenQuestionsMap[questionId] = {
          historyIndex: index,
          historyItem: historyItem,
        };
      }
    }
  });

  // Check for questions that should be unhidden (they were hidden but now shouldn't be)
  const questionsToUnhide = [];
  Object.keys(newHiddenQuestionsMap).forEach((questionId) => {
    const isStillHidden = hiddenQuestions.some(
      (hiddenQue) => hiddenQue._id === questionId
    );

    if (!isStillHidden) {
      // This question should be unhidden
      questionsToUnhide.push(questionId);
    }
  });

  return {
    updatedHistory: updatedQuestionHistory,
    updatedMap: newHiddenQuestionsMap,
    questionsToUnhide,
  };
};

/**
 * Formats answer text for display in history
 * @param {*} answerValue - The answer value
 * @param {string} questionType - Type of question
 * @returns {string} Formatted answer text
 */
export const formatAnswerForHistory = (answerValue, questionType) => {
  if (!answerValue) return "";

  if (questionType === "inventory") {
    return Array.isArray(answerValue)
      ? answerValue.map((ans) => `${ans?.product}: ${ans?.value}`).join(", ")
      : "";
  } else if (questionType === "grid") {
    return "[object Object]";
  } else if (Array.isArray(answerValue)) {
    return answerValue.join(", ") || "";
  } else {
    return answerValue || "";
  }
};

/**
 * Restores an unhidden question to history at the correct position
 * @param {Array} updatedHistory - Current updated history array
 * @param {string} questionId - ID of question to restore
 * @param {Object} allQuestionsOriginal - Original questions array
 * @param {Object} answeresCopy - Copy of Redux answers
 * @param {Object} hiddenQuestionsMap - Map of hidden questions
 * @param {number} currentQuestionIndex - Current question index
 * @returns {Array} Updated history with restored question
 */
export const restoreUnhiddenQuestionToHistory = (
  updatedHistory,
  questionId,
  allQuestionsOriginal,
  answeresCopy,
  hiddenQuestionsMap,
  currentQuestionIndex
) => {
  const hiddenInfo = hiddenQuestionsMap[questionId];
  if (!hiddenInfo) return updatedHistory;

  const question = allQuestionsOriginal.find((q) => q._id === questionId);
  if (!question) return updatedHistory;

  // Find the correct insertion position based on question order in allQuestionsOriginal
  const questionIndexInOriginal = allQuestionsOriginal.findIndex(
    (q) => q._id === questionId
  );

  // Find where to insert by looking for the first question in history
  // that comes AFTER this question in allQuestionsOriginal
  let insertionIndex = updatedHistory.length;

  for (let i = 0; i < updatedHistory.length; i++) {
    const historyItemQuestionId =
      updatedHistory[i]._id || updatedHistory[i].question?._id;

    if (historyItemQuestionId) {
      const historyItemIndexInOriginal = allQuestionsOriginal.findIndex(
        (q) => q._id === historyItemQuestionId
      );

      // If this history item's question comes AFTER the question we want to insert
      if (historyItemIndexInOriginal > questionIndexInOriginal) {
        insertionIndex = i;
        break;
      }
    }
  }

  // Check if question has an answer in Redux
  const hasAnswer = answeresCopy[question._id]?.value !== undefined;

  if (hasAnswer) {
    // Question has an answer, restore with answer
    const answerValue = answeresCopy[question._id].value;
    const answerText = formatAnswerForHistory(
      answerValue,
      question.questionType
    );

    const questionHistoryItem = {
      text: question.text,
      fromUser: false,
      _id: question._id,
      question: question,
    };

    const answerHistoryItem = {
      text: answerText,
      fromUser: true,
      type: question.questionType,
      question: question,
      comment: answeresCopy[question._id].comment || "",
      currentQuestionIndex: currentQuestionIndex,
    };

    updatedHistory.splice(
      insertionIndex,
      0,
      questionHistoryItem,
      answerHistoryItem
    );
  } else {
    // Question has no answer, show question with blank/editable answer
    const questionHistoryItem = {
      text: question.text,
      fromUser: false,
      _id: question._id,
      question: question,
    };

    const answerHistoryItem = {
      text: "",
      fromUser: true,
      type: question.questionType,
      question: question,
      comment: "",
      currentQuestionIndex: currentQuestionIndex,
    };

    updatedHistory.splice(
      insertionIndex,
      0,
      questionHistoryItem,
      answerHistoryItem
    );
  }

  return updatedHistory;
};

/**
 * Adds newly visible questions to history
 * @param {Array} currentHistory - Current question history
 * @param {Set} currentlyVisibleIds - Set of currently visible question IDs
 * @param {Set} willBeVisibleIds - Set of question IDs that will be visible
 * @param {Array} allQuestionsOriginal - Original questions array
 * @param {Array} allQuestions - Current all questions array
 * @param {Object} answeresCopy - Copy of Redux answers
 * @param {number} currentQuestionIndex - Current question index
 * @returns {Array} Updated history with newly visible questions
 */
export const addNewlyVisibleQuestionsToHistory = (
  currentHistory,
  currentlyVisibleIds,
  willBeVisibleIds,
  allQuestionsOriginal,
  allQuestions,
  answeresCopy,
  currentQuestionIndex
) => {
  const newlyVisibleQuestions = [];
  willBeVisibleIds.forEach((qId) => {
    if (!currentlyVisibleIds.has(qId)) {
      newlyVisibleQuestions.push(qId);
    }
  });

  if (newlyVisibleQuestions.length === 0) {
    return currentHistory;
  }

  const updatedHistory = currentHistory.length > 0 ? [...currentHistory] : [];

  newlyVisibleQuestions.forEach((questionId) => {
    // Check if already in history
    const alreadyInHistory = updatedHistory.some(
      (item) => item._id === questionId || item.question?._id === questionId
    );

    if (!alreadyInHistory) {
      const question = allQuestionsOriginal.find((q) => q._id === questionId);

      if (question) {
        const questionIndexInOriginal = allQuestionsOriginal.findIndex(
          (q) => q._id === questionId
        );

        // Find insertion position
        let insertionIndex = updatedHistory.length;

        for (let i = 0; i < updatedHistory.length; i++) {
          const historyItemQuestionId =
            updatedHistory[i]._id || updatedHistory[i].question?._id;

          if (historyItemQuestionId) {
            const historyItemIndexInOriginal = allQuestionsOriginal.findIndex(
              (q) => q._id === historyItemQuestionId
            );

            if (historyItemIndexInOriginal > questionIndexInOriginal) {
              insertionIndex = i;
              break;
            }
          }
        }

        // Check if this question has an answer in Redux
        const questionAnswer = answeresCopy[question._id];
        const hasAnswer = questionAnswer?.value !== undefined;

        // Check if all questions before this one (in original order) are already in history or are being added
        // This helps determine if this question is part of the current flow
        let allPreviousQuestionsInHistory = true;
        for (let i = 0; i < questionIndexInOriginal; i++) {
          const prevQuestion = allQuestionsOriginal[i];
          const prevQuestionInHistory = updatedHistory.some(
            (item) =>
              item._id === prevQuestion._id ||
              item.question?._id === prevQuestion._id
          );
          const prevQuestionHasAnswer =
            answeresCopy[prevQuestion._id]?.value !== undefined;

          // If a previous question was answered but not in history, we have a problem
          if (prevQuestionHasAnswer && !prevQuestionInHistory) {
            allPreviousQuestionsInHistory = false;
            break;
          }
        }

        // Add if:
        // 1. Question has been answered (has value in Redux) - it was answered but hidden, now visible
        // 2. OR it should appear between existing questions in history
        // 3. OR all questions before it are in history (it's the natural next question in flow)
        const shouldAdd =
          hasAnswer || // Question was answered
          insertionIndex < updatedHistory.length || // Should be inserted between existing items
          allPreviousQuestionsInHistory; // Natural progression in survey flow

        if (shouldAdd) {
          const questionHistoryItem = {
            text: question.text,
            fromUser: false,
            _id: question._id,
            question: question,
          };

          let answerText = "";

          if (questionAnswer?.value) {
            answerText = formatAnswerForHistory(
              questionAnswer.value,
              question.questionType
            );
          }

          const answerHistoryItem = {
            text: answerText,
            fromUser: true,
            type: question.questionType,
            question: question,
            comment: questionAnswer?.comment || "",
            currentQuestionIndex: currentQuestionIndex,
          };

          // Insert both question and answer
          updatedHistory.splice(
            insertionIndex,
            0,
            questionHistoryItem,
            answerHistoryItem
          );
        }
      }
    }
  });

  return updatedHistory;
};

/**
 * Filters questions based on location availability
 * @param {Array} questions - Array of questions
 * @param {boolean} hasLocation - Whether location ID exists
 * @returns {Array} Filtered questions
 */
export const filterByLocation = (questions, hasLocation) => {
  if (hasLocation) {
    return questions;
  }

  return questions.filter(
    (item) =>
      item.questionType !== "employees" && item?.questionType !== "inventory"
  );
};

/**
 * Appends additional questions (director message, contact form)
 * @param {Array} questions - Base array of questions
 * @param {Object} surveyConfig - Survey configuration
 * @param {boolean} isContactRequired - Whether contact form is required
 * @returns {Array} Questions with additional items appended
 */
export const appendAdditionalQuestions = (
  questions,
  surveyConfig,
  isContactRequired
) => {
  let allQues = [...questions];

  const needsDirectorMessage =
    surveyConfig?.leadershipUser ||
    surveyConfig?.visit_information ||
    surveyConfig?.competitionInformation;

  if (needsDirectorMessage) {
    allQues = [
      ...allQues,
      { questionType: "director_message", page_break: true },
    ];
  }

  if (isContactRequired) {
    allQues = [...allQues, { questionType: "contact_form" }];
  }

  return allQues;
};

/**
 * Adjusts current question index after hiding/unhiding
 * @param {Object} currentQuestion - The current question object
 * @param {Array} newFilteredQuestions - New filtered questions array
 * @param {number} currentIndex - Current question index
 * @returns {number|null} New index or null if no adjustment needed
 */
export const calculateAdjustedQuestionIndex = (
  currentQuestion,
  newFilteredQuestions,
  currentIndex
) => {
  if (!currentQuestion?._id) {
    return null;
  }

  // Find what the new index should be in newFilteredQuestions for the same question
  const newIndex = newFilteredQuestions.findIndex(
    (q) => q._id === currentQuestion._id
  );

  // Only update if the question still exists and index has changed
  if (newIndex !== -1 && newIndex !== currentIndex) {
    return newIndex;
  } else if (newIndex === -1) {
    // Current question was hidden, keep at same position or adjust if out of bounds
    if (currentIndex >= newFilteredQuestions.length) {
      return Math.max(0, newFilteredQuestions.length - 1);
    }
  }

  return null;
};

/**
 * Calculates the appropriate question index after editing a question
 * @param {number} editedQuestionIndex - Index of the question that was edited (in old allQuestions array)
 * @param {Array} newFilteredQuestions - New filtered questions array after hide/unhide
 * @param {Array} oldQuestions - Previous allQuestions array before filtering
 * @param {number} previousCurrentIndex - The current question index before editing
 * @returns {number} New index to show
 */
export const calculatePostEditQuestionIndex = (
  currentQuestionId, // ID of the question that was being displayed before editing
  newFilteredQuestions,
  oldQuestions,
  previousCurrentIndex
) => {
  // If no questions left, return 0
  if (!newFilteredQuestions || newFilteredQuestions.length === 0) {
    return 0;
  }

  // If the previous current index was at or beyond the last question,
  // it means all questions were answered, so return the same state (end of survey)
  if (previousCurrentIndex >= oldQuestions.length) {
    return newFilteredQuestions.length;
  }

  // Find the question that was being displayed BEFORE editing started
  // This is the question the user should return to after editing
  if (currentQuestionId) {
    const newIndexOfCurrentQuestion = newFilteredQuestions.findIndex(
      (q) => q._id === currentQuestionId
    );

    // If we found it in the new array, return its new position
    if (newIndexOfCurrentQuestion !== -1) {
      return newIndexOfCurrentQuestion;
    }
  }

  // Fallback: if the current question was somehow hidden or not found,
  // find what question was at previousCurrentIndex and locate it in the new array
  const previousCurrentQuestion = oldQuestions[previousCurrentIndex];
  if (previousCurrentQuestion) {
    const newIndexOfPreviousQuestion = newFilteredQuestions.findIndex(
      (q) => q._id === previousCurrentQuestion._id
    );
    if (newIndexOfPreviousQuestion !== -1) {
      return newIndexOfPreviousQuestion;
    }
  }

  // Last resort fallback
  return Math.min(newFilteredQuestions.length - 1, previousCurrentIndex);
};
